import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';

import { PAY_MEAN_CODE } from '@ecp/common/src/const/interface/payMeanCode';
import { INICIS_PAY_TYPE_MOBILE } from '@ecp/common/src/const/interface/inicisPayType';
import { CARD_COMPANY_CODE } from '@ecp/common/src/const/interface/cardCompanyCode';
import { NO_INTEREST_DIVISION_CODE } from '@ecp/common/src/const/interface/noInterestDivisionCode';
import { PAYCO_PAY_TYPE } from '@ecp/common/src/const/interface/paycoPayType';

import { checkOs, OS_TYPE } from '@mo-utils/commonUtils';
import { withMobileStatus } from '@mo-recoil/system/selector';
import inicisApi from '@mo-apis/interface/inicisApi';
import paycoApi from '@mo-apis/interface/paycoApi';

const MobilePayForm = ({ data, payType = {}, onComplete }) => {
  const [payData, setPayData] = useState({});
  // [WIP-32635] 같은 주문 폼이 두 번 제출되지 않도록 마지막 실행 키를 보관합니다.
  const submittedPaymentKeyRef = useRef(null);
  // [WIP-32635] 최신 응답이 입력된 이니시스 폼을 직접 제출하기 위해 참조를 보관합니다.
  const inicisFormRef = useRef(null);
  const { isMobileApp } = useRecoilValue(withMobileStatus);

  useEffect(() => {
    if (!payType || !data || Object.keys(data).length === 0) return undefined;

    // [WIP-32635] 결제수단이나 주문이 바뀐 뒤 도착하는 이전 응답을 구분합니다.
    let isActive = true;

    const loadPayment = async () => {
      try {
        if (Object.values(INICIS_PAY_TYPE_MOBILE).includes(payType)) {
          // [WIP-32635] 간편결제 종류를 함께 보내 현재 결제수단에 맞는 인증값을 받습니다.
          const { result } = await inicisApi.getInicisInformation({ ...data, cardType: payType.cardType });
          // [WIP-32635] 최신 주문에서 시작한 요청이 아니면 응답을 사용하지 않습니다.
          if (!isActive) return;
          if (!result?.mid || !result?.actionUrl) {
            // [WIP-32635] 필수 인증값이 없으면 결제 버튼 잠금을 풀어 재시도할 수 있게 합니다.
            onComplete?.();
            return;
          }

          // [WIP-32635] 결제를 다시 시도해도 현재 주문의 응답만 사용합니다.
          setPayData({ ...result, requestedOrderId: data.oid || data.orderNo });
        } else if (PAYCO_PAY_TYPE === payType) {
          // [WIP-32635] 전달받은 주문 객체를 바꾸지 않고 PAYCO 전용 요청 값을 새 객체에 담습니다.
          const paycoRequestData = {
            ...data,
            inAppYn: isMobileApp ? 'Y' : 'N',
            orderChannel: 'MOBILE',
          };
          const { result } = await paycoApi.createPaycoOrderReservation(paycoRequestData);
          // [WIP-32635] 다른 결제로 넘어간 뒤 도착한 PAYCO 응답은 무시합니다.
          if (!isActive) return;
          if (!result?.orderSheetUrl) {
            // [WIP-32635] 이동할 결제 주소가 없으면 잠금을 풀고 현재 주문 화면을 유지합니다.
            onComplete?.();
            return;
          }

          // [WIP-32635] 외부 결제 화면으로 이동하기 전에 결제 버튼 잠금을 해제합니다.
          onComplete?.();
          window.location.href = result.orderSheetUrl;
        } else {
          // [WIP-32635] 일반 이니시스 결제도 현재 주문 정보로 인증값을 새로 요청합니다.
          const { result } = await inicisApi.getInicisInformation(data);
          if (!isActive) return;
          if (!result?.mid || !result?.actionUrl) {
            // [WIP-32635] 결제 폼을 만들 수 없는 응답이면 버튼 잠금을 해제합니다.
            onComplete?.();
            return;
          }

          setPayData({ ...result, requestedOrderId: data.oid || data.orderNo });
        }
      } catch {
        if (isActive) {
          // [WIP-32635] 결제 준비 API가 실패해도 결제 버튼이 계속 잠기지 않게 합니다.
          onComplete?.();
        }
      }
    };

    loadPayment();

    return () => {
      // [WIP-32635] 새 요청이 시작되면 이전 비동기 작업을 오래된 요청으로 표시합니다.
      isActive = false;
    };
  }, [data, isMobileApp, payType, onComplete]);

  useEffect(() => {
    const requestedOrderId = data.oid || data.orderNo;
    // [WIP-32635] 응답에 저장한 주문번호가 현재 주문과 같을 때만 폼을 제출합니다.
    if (!payData.mid || payData.requestedOrderId !== requestedOrderId) return;

    // [WIP-32635] 요청 주문번호와 이니시스 주문번호를 묶어 실행 키를 만듭니다.
    const paymentKey = `${payData.requestedOrderId}-${payData.orderNo || payData.oid}`;
    if (submittedPaymentKeyRef.current === paymentKey) return;

    const inicisForm = inicisFormRef.current;
    if (!inicisForm) {
      // [WIP-32635] 제출할 폼이 없으면 결제 잠금을 풀어 다음 시도를 받을 수 있게 합니다.
      onComplete?.();
      return;
    }

    try {
      // [WIP-32635] 같은 결제수단도 새 주문번호라면 결제 화면을 다시 엽니다.
      submittedPaymentKeyRef.current = paymentKey;
      // [WIP-32635] 모바일 결제는 새 창을 겹쳐 열지 않고 현재 화면에서 진행합니다.
      inicisForm.setAttribute('target', '_self');
      inicisForm.setAttribute('accept-charset', 'euc-kr');
      inicisForm.submit();
      // [WIP-32635] 폼 제출이 시작되면 결제 버튼 잠금을 해제합니다.
      onComplete?.();
    } catch {
      // [WIP-32635] 제출 실패 시 실행 키를 비워 같은 결제를 다시 시도할 수 있게 합니다.
      submittedPaymentKeyRef.current = null;
      onComplete?.();
    }
  }, [data.oid, data.orderNo, onComplete, payData]);

  const convertedQuotaBase = useMemo(
    () => (data.cardCompanyNo === '12' ? '02:03:04:05:06:07:08:09:10:11:12:18:24' : '02:03:04:05:06:07:08:09:10:11:12'),
    [data]
  );

  const cardCompanyNo = useMemo(() => {
    // [WIP-32635] 군인공제회 제휴카드는 이니시스에서 사용하는 일반 카드사 코드로 바꿉니다.
    if (data.cardCompanyNo === CARD_COMPANY_CODE.MILITARY_MUTUAL_AID_ASSOCIATION_KB_CARD) {
      return CARD_COMPANY_CODE.KB_CARD;
    }

    if (data.cardCompanyNo === CARD_COMPANY_CODE.MILITARY_MUTUAL_AID_ASSOCIATION_SHINHAN_CARD) {
      return CARD_COMPANY_CODE.SHINHAN_CARD;
    }

    return data.cardCompanyNo;
  }, [data.cardCompanyNo]);

  const noInterestMonth = useMemo(
    () =>
      data.noInterestDivisionCode === NO_INTEREST_DIVISION_CODE.NO_INTEREST
        ? '&merc_noint=Y&noint_quota=' + data.noInterestMonth
        : '',
    [data.noInterestDivisionCode, data.noInterestMonth]
  );

  const pReserved = useMemo(() => {
    if (payType && checkOs() === OS_TYPE.IOS) {
      // [WIP-32635] 예약값이 없는 결제수단도 undefined 문자열 없이 iOS 앱 복귀 값을 만듭니다.
      return (payType.pReserved || '') + '&ismart_use_sign=Y&mall_app_name=WSMAPP://?paymethod;' + noInterestMonth;
    }
    return payType?.pReserved;
  }, [payType, noInterestMonth]);

  const isInicis = useMemo(() => {
    return Object.values(INICIS_PAY_TYPE_MOBILE).includes(payType);
  }, [payType]);
  return (
    <div className="inicis-mobile">
      {isInicis && (
        <form
          ref={inicisFormRef}
          id="inicis-mobile-form"
          name=""
          method="Post"
          action={payData.actionUrl}
          acceptCharset={'euc-kr'}
        >
          <input type="hidden" readOnly name="P_INI_PAYMENT" value={payType.iniPayment} />
          <input type="hidden" readOnly name="P_MID" value={payData.mid || ''} />
          <input type="hidden" readOnly name="P_OID" value={payData.orderNo || ''} />
          <input type="hidden" readOnly name="P_AMT" value={payData.price || ''} />
          <input type="hidden" readOnly name="P_GOODS" value={payData.goodName || ''} />
          <input type="hidden" readOnly name="P_UNAME" value={payData.buyerName || ''} />
          <input type="hidden" readOnly name="P_NEXT_URL" value={payData.nextUrl || ''} />
          {/*<input type="hidden" readOnly name="P_NOTI_URL" value={payType} /> /!* 가상계좌 입금통보 URL *!/*/}
          <input type="hidden" readOnly name="P_HPP_METHOD" value={payData.hppMethod || ''} />
          <input type="hidden" readOnly name="P_QUOTABASE" value={convertedQuotaBase} />
          {/*<input type="hidden" readOnly name="P_CARD_OPTION" value={'selcode=' + payData.signature || ''} />*/}
          <input type="hidden" readOnly name="P_ONLY_CARDCODE" value={cardCompanyNo || ''} />
          <input type="hidden" readOnly name="P_VBANK_DT" value={''} />
          <input type="hidden" readOnly name="P_TAX" value={''} />
          <input type="hidden" readOnly name="P_TAXFREE" value={''} />
          <input type="hidden" readOnly name="P_MOBILE" value={data.buyerTel || ''} />
          <input type="hidden" readOnly name="P_EMAIL" value={payData.buyerEmail || ''} />
          <input type="hidden" readOnly name="P_MNAME" value={'welstorymall'} />
          {/*<input type="hidden" readOnly name="P_LOGO_URL" value={payData.signature || ''} />*/}
          <input type="hidden" readOnly name="P_NOTI" value={payData.noti || ''} />
          <input type="hidden" readOnly name="P_RESERVED" value={pReserved || ''} />
          <input type="hidden" readOnly name="P_CHARSET" value={'utf8'} />
        </form>
      )}
    </div>
  );
};

export const inicisData = PropTypes.shape({
  goodName: PropTypes.string, //상품명
  buyerName: PropTypes.string, //구매자명
  buyerTel: PropTypes.string, // 구매자핸드폰번호
  buyerEmail: PropTypes.string, //구매자 이메일
  price: PropTypes.number, // 금액
  oid: PropTypes.string, // 주문번호
  orderNo: PropTypes.string, // 주문번호
  payMeanCode: PropTypes.oneOf([Object.values(PAY_MEAN_CODE)]), //지불수단코드
  cardPointUseYn: PropTypes.string, // 카드포인트사용여부
  cardCompanyNo: PropTypes.oneOf([...Object.values(CARD_COMPANY_CODE)]), //카드회사번호
  noInterestDivisionCode: PropTypes.string, //무이자구분코드
  escrowYn: PropTypes.oneOf(['Y', 'N']), //에스크로여부
  scheduledDeliveryYn: PropTypes.oneOf(['Y', 'N']), //정기배송여부
  noInterestMonth: PropTypes.string,
  noInterest: PropTypes.oneOf([...Object.values(NO_INTEREST_DIVISION_CODE)]),
  quotaBase: PropTypes.string,
  payNo: PropTypes.string,
  tempOrderNo: PropTypes.string,
  cartDiviCd: PropTypes.string, // AS-IS에서 넘기는 값이던데...
  cartNoList: PropTypes.array, // AS-IS에서 넘기는 값이던데...
  orderTypeCode: PropTypes.string, // 주문유형
});

export const paycoData = PropTypes.shape({
  tempOrderNumber: PropTypes.string,
  orderNumber: PropTypes.string,
  paymentNumber: PropTypes.string,
  orderChannel: PropTypes.string,
  inAppYn: PropTypes.string,
  memberId: PropTypes.string,
  cartDiviCd: PropTypes.string, // AS-IS에서 넘기는 값이던데...
  cartNoList: PropTypes.array, // AS-IS에서 넘기는 값이던데...
  orderTypeCode: PropTypes.string, // 주문유형
});
MobilePayForm.propTypes = {
  data: PropTypes.oneOfType([inicisData, paycoData]),
  payType: PropTypes.oneOf([...Object.values(INICIS_PAY_TYPE_MOBILE), PAYCO_PAY_TYPE]),
  onComplete: PropTypes.func,
};

export default MobilePayForm;
